import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { categoriesApi, productsService } from '@/services/api';
import { ArrowLeft, Image, Package, DollarSign, Type, Check, X, Plus } from 'lucide-react';
//import { parse } from 'path';

// Esquema de validación
const productSchema = z.object({
    name: z.string()
        .min(3, 'El nombre debe tener al menos 3 caracteres')
        .max(100, 'El nombre no puede exceder 100 caracteres'),
    description: z.string()
        .min(10, 'La descripción debe tener al menos 10 caracteres')
        .max(2000, 'La descripción no puede exceder 2000 caracteres'),
    short_description: z.string().optional(),
    price: z.number()
        .min(0.01, 'El precio debe ser mayor a 0')
        .max(100000, 'El precio no puede exceder 100,000'),
    stock_quantity: z.number()
        .min(0, 'El stock no puede ser negativo')
        .max(10000, 'El stock no puede exceder 10,000'),
    sku: z.string()
        .min(3, 'El SKU debe tener al menos 3 caracteres')
        .max(50, 'El SKU no puede exceder 50 caracteres'),
    category_id: z.number().min(1, 'Selecciona una categoría'),
});

type ProductFormData = z.infer<typeof productSchema>;

const SellerProductFormPage: React.FC = () => {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [images, setImages] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [categories, setCategories] = useState<any>([]);
    useEffect(() => {
        categoriesApi.getCategoriesForSeller({}).then(data => setCategories(data));
    }, [])
    const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<ProductFormData>({
        resolver: zodResolver(productSchema),
        defaultValues: {
            price: 0,
            stock_quantity: 0,
        }
    });

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            const validImages = files.filter(file =>
                file.type.match('image.*') && file.size <= 5 * 1024 * 1024
            );

            if (validImages.length !== files.length) {
                toast.error('Algunas imágenes no son válidas (solo JPG/PNG, max 5MB)');
            }

            setImages(prev => [...prev, ...validImages]);

            // Crear previews
            validImages.forEach(file => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    if (e.target?.result) {
                        setImagePreviews(prev => [...prev, e.target?.result as string]);
                    }
                };
                reader.readAsDataURL(file);
            });
        }
    };

    const removeImage = (index: number) => {
        setImages(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const onSubmit = async (data: ProductFormData) => {
        setIsSubmitting(true);
        try {
            // Primero creamos el producto
            const product = await productsService.createProduct({
                name: data.name,
                description: data.description,
                short_description: data.short_description,
                price: data.price,
                stock_quantity: data.stock_quantity,
                sku: data.sku,
                category_id: data.category_id,
                requires_shipping: 1,
                status: 'active'
            });

            // Luego subimos las imágenes si hay
            if (images.length > 0) {
                await productsService.uploadProductImages(product.id, images);
            }

            toast.success('Producto creado exitosamente');
            navigate('/seller/products');
        } catch (error) {
            console.error('Error creating product:', error);
            toast.error('Error al crear el producto');
        } finally {
            setIsSubmitting(false);
        }
    };
    const generateUniqueSKU = (productName: string, categoryId: number, existingSkus: string[] = []): string => {
        // Limpiar el nombre del producto
        const cleanName = productName
            .normalize('NFD') // Normalizar caracteres especiales
            .replace(/[\u0300-\u036f]/g, '') // Eliminar acentos
            .replace(/[^a-zA-Z0-9]/g, '') // Eliminar caracteres especiales
            .substring(0, 3) // Tomar primeros 3 caracteres
            .toUpperCase();

        // Obtener código de categoría (podrías tener un mapeo de IDs a códigos)
        const categoryCode = `CAT-${categoryId.toString().padStart(3, '0')}`;

        // Timestamp reducido (últimos 6 dígitos)
        const timestamp = Date.now().toString().slice(-6);

        // Generar SKU base
        let sku = `${cleanName}-${categoryCode}-${timestamp}`;
        let counter = 1;

        // Verificar si el SKU ya existe y generar variantes si es necesario
        while (existingSkus.includes(sku)) {
            sku = `${cleanName}-${categoryCode}-${timestamp}-${counter.toString().padStart(2, '0')}`;
            counter++;
        }
        return sku;
    };
    //const [sku, setSku] = useState('');

    // Cargar SKUs existentes al montar el componente
    /*useEffect(() => {
        productsApi.getProducts().then(response => {
            const skus = response.data.map(product => product.sku);
            setExistingSkus(skus);
        });
    }, []);*/

    // Generar SKU cuando cambia el nombre o categoría
    const handleGenerateSku = (productName: string, categoryId: number) => {
        const newSku = generateUniqueSKU(productName, categoryId, []);
        //setSku(newSku);
        setValue('sku', newSku); // Si estás usando react-hook-form
    };
    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="mb-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center text-primary-600 hover:text-primary-800 mb-4"
                    >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Volver al panel
                    </button>

                    <h1 className="text-2xl font-bold text-gray-900">
                        <Package className="inline-block w-6 h-6 mr-2 text-primary-600" />
                        Nuevo Producto
                    </h1>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Sección de imágenes */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <Image className="w-5 h-5 mr-2 text-blue-600" />
                            Imágenes del Producto
                        </h2>

                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-4">
                            {imagePreviews.map((preview, index) => (
                                <div key={index} className="relative group">
                                    <img
                                        src={preview}
                                        alt={`Preview ${index}`}
                                        className="w-full h-32 object-cover rounded-lg border border-gray-200"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeImage(index)}
                                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}

                            <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-500 transition-colors">
                                <input
                                    type="file"
                                    multiple
                                    onChange={handleImageUpload}
                                    className="hidden"
                                    accept="image/*"
                                />
                                <Plus className="w-8 h-8 text-gray-400 mb-2" />
                                <span className="text-sm text-gray-600">Agregar imágenes</span>
                            </label>
                        </div>
                        <p className="text-xs text-gray-500">
                            Máximo 10 imágenes. Formatos: JPG, PNG. Tamaño máximo: 5MB cada una.
                        </p>
                    </div>

                    {/* Información básica */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <Type className="w-5 h-5 mr-2 text-primary-600" />
                            Información Básica
                        </h2>

                        <div className="grid grid-cols-1 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nombre del Producto *
                                </label>
                                <input
                                    {...register('name')}
                                    onChange={(e) => handleGenerateSku(e.target.value, watch('category_id'))}
                                    //onChange={(e) => handleGenerateSku(e.target.value, categoryId)}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.name ? 'border-red-300' : 'border-gray-300'}`}
                                    placeholder="Ej: Smartphone XYZ 2023"
                                />
                                {errors.name && (
                                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Descripción *
                                </label>
                                <textarea
                                    {...register('description')}
                                    rows={4}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.description ? 'border-red-300' : 'border-gray-300'}`}
                                    placeholder="Describe detalladamente tu producto..."
                                />
                                {errors.description && (
                                    <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Descripción Corta
                                </label>
                                <textarea
                                    {...register('short_description')}
                                    rows={1}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 border-gray-300"
                                    placeholder="Describe brevemente tu producto..."
                                />
                            </div>
                        </div>
                    </div>

                    {/* Precio e inventario */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <DollarSign className="w-5 h-5 mr-2 text-green-600" />
                            Precio e Inventario
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Precio (PEN) *
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">S/ </span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        {...register('price', { valueAsNumber: true })}
                                        onChange={(e) => setValue('price', parseFloat(e.target.value) || 0)}
                                        className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.price ? 'border-red-300' : 'border-gray-300'}`}
                                        placeholder="0.00"
                                    />
                                </div>
                                {errors.price && (
                                    <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Cantidad en Stock *
                                </label>
                                <input
                                    type="number"
                                    {...register('stock_quantity', { valueAsNumber: true })}
                                    onChange={(e) => setValue('stock_quantity', parseInt(e.target.value) || 0)}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.stock_quantity ? 'border-red-300' : 'border-gray-300'}`}
                                    placeholder="0"
                                />
                                {errors.stock_quantity && (
                                    <p className="mt-1 text-sm text-red-600">{errors.stock_quantity.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    SKU (Código de producto) *
                                </label>
                                <input
                                    {...register('sku')}
                                    readOnly
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.sku ? 'border-red-300' : 'border-gray-300'}`}
                                    placeholder="Ej: PROD-001"
                                />
                                {errors.sku && (
                                    <p className="mt-1 text-sm text-red-600">{errors.sku.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Categoría *
                                </label>
                                <select
                                    {...register('category_id', { valueAsNumber: true })}
                                    onChange={(e) => handleGenerateSku(watch('name'), parseInt(e.target.value))}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.category_id ? 'border-red-300' : 'border-gray-300'}`}
                                >
                                    <option value="">Selecciona una categoría</option>
                                    {categories.map((category: any) => (
                                        <option key={category.id} value={category.id}>
                                            {category.name}
                                        </option>
                                    ))}
                                    {/* Las categorías reales deberían venir de una API */}
                                </select>
                                {errors.category_id && (
                                    <p className="mt-1 text-sm text-red-600">{errors.category_id.message}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Botones de acción */}
                    <div className="flex justify-end space-x-4">
                        <button
                            type="button"
                            onClick={() => navigate('/seller/products')}
                            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                        >
                            {isSubmitting ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <Check className="w-4 h-4 mr-2" />
                                    Guardar Producto
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SellerProductFormPage;